// DefaultLayout.jsx
import React, {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  createContext,
  useMemo,
  useCallback,
} from "react";
import {
  Input,
  Select,
  Button,
  Layout,
  Menu,
  Dropdown,
  message,
  Tooltip,
  Modal,
  Tag,
} from "antd";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import config from "../config";
import * as faceapi from "face-api.js";
import {
  CaretDownOutlined,
  CaretUpOutlined,
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  UserOutlined,
  HomeOutlined,
  FileTextOutlined,
  LaptopOutlined,
  DesktopOutlined,
  SettingOutlined,
  PrinterOutlined,
  SearchOutlined,
  InfoCircleOutlined,
  DownSquareOutlined,
} from "@ant-design/icons";
import "../styles/DefaultLayout.css";
import { getUserRole, capitalize } from "../utils/auth";
import Swal from "sweetalert2";
import { jwtDecode } from "jwt-decode";
import AccessKeyModal from "./checkAccessKey";
import CloseButton from "../components/CloseButton";
import logo from "../assets/images/mkfnblogo.png";

const { Header, Sider, Content } = Layout;
const SubMenu = Menu.SubMenu;

// Modal blur context (kept at module scope)
export const ModalBlurContext = createContext({
  incrementModal: () => { },
  decrementModal: () => { },
});

/* UI / Theme constants */
const COLORS = {
  brand: "rgb(229,103,23)",
  brandText: "#dc6c04",
  white: "#fff",
  grayText: "#888",
  lightBorder: "#f0f0f0",
  text: "#333",
  badge: "#1677ff",
  unknown: "#ff4d4f",
  good: "#52c41a",
};

/* face-api constants */
const TINY_OPTIONS = { inputSize: 416, scoreThreshold: 0.7 };
const MATCH_THRESHOLD = 0.52;

/* Liveness thresholds */
const MOVEMENT_THRESHOLD_PX = 40;
const MOVEMENT_WINDOW_MS = 2000;
const MOVEMENT_COOLDOWN_MS = 700;

const RECOG_COOLDOWN_MS = 5000;

let _modelsLoaded = false;
let _modelsLoading = false;
let _modelsLoadPromise = null;

const loadFaceModelsOnce = async () => {
  if (_modelsLoaded) return true;
  if (_modelsLoading && _modelsLoadPromise) return _modelsLoadPromise;

  _modelsLoading = true;
  _modelsLoadPromise = (async () => {
    try {
      // Choose model URL depending on environment (Electron packaged or web)
      const isElectron =
        typeof window !== "undefined" &&
        typeof window.process === "object" &&
        window.process.type === "renderer";
      const modelUrl = isElectron ? "./models" : (process.env.PUBLIC_URL || "") + "/models";

      // Attempt to load required nets
      await Promise.all([
        faceapi.nets.tinyFaceDetector.loadFromUri(modelUrl),
        faceapi.nets.faceLandmark68Net.loadFromUri(modelUrl),
        faceapi.nets.faceRecognitionNet.loadFromUri(modelUrl),
      ]);

      _modelsLoaded = true;
      _modelsLoading = false;
      console.log("✅ Face models loaded (singleton) from", modelUrl);
      return true;
    } catch (err) {
      console.error("❌ Failed to load face models (singleton)", err);
      _modelsLoaded = false;
      _modelsLoading = false;
      return false;
    }
  })();

  return _modelsLoadPromise;
};

const areModelsLoaded = () => _modelsLoaded;

const tryGetIpcRenderer = () => {
  try {
    if (typeof window !== "undefined" && window.require) {
      const { ipcRenderer } = window.require("electron");
      return ipcRenderer;
    }
    if (typeof window !== "undefined" && window.ipcRenderer) {
      return window.ipcRenderer;
    }
  } catch (err) {
    // ignore
  }
  return null;
};

/* DefaultLayout component */
const DefaultLayout = forwardRef(function DefaultLayout(
  {
    children,
    onSearch,
    searchPlaceholder,
    searchValue,
    showSearch = true,
    contentStyle,
    customSearchComponent,
    showSlug,
  },
  ref
) {
  const apiBaseUrl = config.REACT_APP_API_BASE_URL;

  const navigate = useNavigate();
  const location = useLocation();

  // --- Search (internal state + sync from controlled prop if provided) ---
  const [searchText, setSearchText] = useState("");
  useEffect(() => {
    // if parent passes searchValue (controlled usage), sync it
    if (typeof searchValue !== "undefined" && searchValue !== searchText) {
      setSearchText(searchValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  const handleSearch = (e) => {
    const v = e?.target?.value ?? "";
    setSearchText(v);
    onSearch && onSearch(v);
  };

  // printers
  const [settings, setSettings] = useState({});
  const hasInitialized = useRef(false);
  const [selectedPrinterId, setPrinterId] = useState(null);
  const [printersData, setPrintersData] = useState([]);
  const [printerStatusOpen, setPrinterStatusOpen] = useState(false);
  const handlePrinterStatusDropdownVisibleChange = (open) => setPrinterStatusOpen(open);
  const handlePrinterChange = (value) => {
    if (value != null) {
      setPrinterId(value);
      localStorage.setItem("selectedPrinterId", value);
    }
  };

  const getAllPrinter = async () => {
    try {
      const { data } = await axios.get(`${apiBaseUrl}/api/settings/get-settings`);
      const printerList = data.printerSettings;
      if (printerList) {
        const counterPrinter = printerList.filter((printer) => printer.is_counter);
        setPrintersData(counterPrinter);
        setSettings(data || {});
      }
    } catch (error) {
      message.error("Something Went Wrong");
      console.error("getAllPrinter error", error);
    }
  };

  useEffect(() => {
    getAllPrinter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Only run initialization once when printersData arrives
    if (!hasInitialized.current && printersData.length > 0) {
      const checkInitSelectedPrinterId = () => {
        const savedPrinterId = localStorage.getItem("selectedPrinterId");

        if (savedPrinterId === null) {
          const firstPrinterId = printersData[0].id;
          setPrinterId(firstPrinterId);
          localStorage.setItem("selectedPrinterId", firstPrinterId);
        } else {
          const printer = printersData.find((printer) => printer.id === savedPrinterId);

          if (printer) {
            setPrinterId(savedPrinterId);
          } else {
            const firstPrinterId = printersData[0].id;
            setPrinterId(firstPrinterId);
            localStorage.setItem("selectedPrinterId", firstPrinterId);
          }
        }
      };

      checkInitSelectedPrinterId();
      hasInitialized.current = true;
    }
  }, [printersData]);

  // layout/auth
  const [collapsed, setCollapsed] = useState(false);
  const toggleCollapse = () => setCollapsed((p) => !p);
  const [currentUser, setCurrentUser] = useState(null);
  const [openKeys, setOpenKeys] = useState([]);
  const [isUserHovered, setIsUserHovered] = useState(false);

  const menuItems = useMemo(
    () => [
      {
        key: "1",
        label: "Logout",
        onClick: () => {
          localStorage.removeItem("auth");
          navigate("/login");
        },
      },
    ],
    [navigate]
  );

  useEffect(() => {
    const authRaw = localStorage.getItem("auth");
    if (!authRaw) {
      navigate("/login");
      return;
    }
    try {
      const auth = JSON.parse(authRaw);
      if (auth?.token) {
        const decoded = jwtDecode(auth.token);
        const username = decoded?.name || decoded?.userId || decoded?.user || "";
        setCurrentUser(username);
        if (window.location.pathname === "/login") navigate("/");
      } else {
        navigate("/login");
      }
    } catch (err) {
      localStorage.removeItem("auth");
      navigate("/login");
    }
  }, [navigate]);

  useEffect(() => {
    const settingsChildren = [
      "/general",
      "/tables",
      "/category",
      "/items",
      "/inventory",
      "/inventoryLog",
      "/setTax",
      "/setService",
      "/invoice",
      "/staff",
      "/clockInOut",
      "/productIcon",
      "/preferences",
    ];
    const posChildren = ["/", "/cashiers", "/staffTransactionsRecord"];
    if (posChildren.includes(location.pathname)) setOpenKeys(["POS"]);
    else if (settingsChildren.includes(location.pathname)) setOpenKeys(["SubMenu"]);
    else setOpenKeys([]);
  }, [location.pathname]);

  const onOpenChange = (keys) => {
    const latestOpenKey = keys.find((k) => !openKeys.includes(k));
    if (latestOpenKey === "POS") setOpenKeys(latestOpenKey ? [latestOpenKey] : []);
    else if (latestOpenKey === "SubMenu") setOpenKeys(latestOpenKey ? [latestOpenKey] : []);
    else setOpenKeys(keys);
  };

  // modal blur context
  const [openModalCount, setOpenModalCount] = useState(0);
  const incrementModal = () => setOpenModalCount((c) => c + 1);
  const decrementModal = () => setOpenModalCount((c) => Math.max(0, c - 1));

  // refs
  const accessKeyModalRef = useRef();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  // debug
  const [debugMode, setDebugMode] = useState(false);
  const log = (...args) => debugMode && console.log("[DL DEBUG]", ...args);
  const warn = (...args) => debugMode && console.warn("[DL WARN]", ...args);

  // face-api models loaded (component-level mirror of module singleton)
  const [modelsLoaded, setModelsLoaded] = useState(areModelsLoaded());
  const [modelsLoading, setModelsLoading] = useState(false);

  // load models via module-level loader and mirror state locally
  const ensureModels = useCallback(async () => {
    if (areModelsLoaded()) {
      setModelsLoaded(true);
      setModelsLoading(false);
      return true;
    }
    setModelsLoading(true);
    const ok = await loadFaceModelsOnce();
    setModelsLoaded(ok);
    setModelsLoading(false);
    if (!ok) {
      message.error("Failed to load face models");
    } else {
      message.success("Face models loaded");
    }
    return ok;
  }, []);

  // webcam helpers
  const startWebcam = async () => {
    const v = videoRef.current;
    if (!v) return;
    try {
      // prefer resolution that face-api performs well with
      const stream = await navigator.mediaDevices.getUserMedia({ video: { width: 640, height: 480 } });
      v.srcObject = stream;
      await v.play();
      log("webcam started");
    } catch (err) {
      console.error("webcam error", err);
      message.error("Please allow webcam access");
      throw err;
    }
  };
  const stopWebcam = () => {
    const v = videoRef.current;
    if (!v || !v.srcObject) return;
    try {
      const tracks = v.srcObject.getTracks();
      tracks.forEach((t) => t.stop && t.stop());
      v.srcObject = null;
      log("webcam stopped");
    } catch (err) {
      console.warn("stopWebcam err", err);
    }
  };

  // staff list/descriptors
  const [staffList, setStaffList] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [staffDescriptors, setStaffDescriptors] = useState([]);

  const fetchStaffList = async () => {
    setLoadingStaff(true);
    try {
      const res = await axios.get(`${apiBaseUrl}/api/users/get-user`);
      const arr = Array.isArray(res.data) ? res.data : [];
      const normalized = arr.map((u) => ({
        _id: u._id || u.id || u.userId || Math.random().toString(36).slice(2),
        userId: String(u.userId || u._id || u.id || ""),
        name: u.name || u.userId || "Unknown",
        image: u.image || null,
      }));
      setStaffList(normalized);
      log("staff list fetched", normalized.length);
      return normalized;
    } catch (err1) {
      try {
        const res2 = await axios.get(`${apiBaseUrl}/api/staff`);
        const arr2 = Array.isArray(res2.data) ? res2.data : [];
        const normalized2 = arr2.map((u) => ({
          _id: u._id || u.id || u.userId || Math.random().toString(36).slice(2),
          userId: String(u.userId || u._id || u.id || ""),
          name: u.name || u.userId || "Unknown",
          image: u.image || u.photoUrl || null,
        }));
        setStaffList(normalized2);
        log("staff list fallback", normalized2.length);
        return normalized2;
      } catch (err2) {
        console.error("fetch staff both failed", err1, err2);
        setStaffList([]);
        return [];
      } finally {
        setLoadingStaff(false);
      }
    } finally {
      setLoadingStaff(false);
    }
  };

  const computeStaffDescriptorsLocal = async (list) => {
    if (!areModelsLoaded()) {
      warn("models not loaded, skipping descriptor compute");
      return [];
    }
    if (!list || list.length === 0) {
      setStaffDescriptors([]);
      return [];
    }
    const results = await Promise.all(
      list.map(async (s) => {
        if (!s.image) {
          warn("skip staff no image", s.userId);
          return null;
        }
        try {
          // Use backend proxy to avoid CORS issues if necessary
          const proxiedUrl = `${apiBaseUrl}/api/proxy-image?url=${encodeURIComponent(s.image)}`;
          let imgEl;
          try {
            imgEl = await faceapi.fetchImage(proxiedUrl);
          } catch (errFetch) {
            warn("fetchImage failed", s.userId, errFetch?.message || errFetch);
            return null;
          }
          try {
            const det = await faceapi
              .detectSingleFace(imgEl, new faceapi.TinyFaceDetectorOptions(TINY_OPTIONS))
              .withFaceLandmarks()
              .withFaceDescriptor();
            if (det?.descriptor) {
              return {
                userId: s.userId,
                name: s.name,
                image: s.image,
                descriptor: det.descriptor,
              };
            } else {
              warn("no face in staff image", s.userId);
              return null;
            }
          } catch (errProc) {
            warn("descriptor compute failed", s.userId, errProc?.message || errProc);
            return null;
          }
        } catch (err) {
          warn("process staff image failed", s.userId, err?.message || err);
          return null;
        }
      })
    );
    const filtered = results.filter(Boolean);
    setStaffDescriptors(filtered);
    log("staff descriptors prepared", filtered.length);
    return filtered;
  };

  // build face matcher
  const buildFaceMatcher = () => {
    const labeled = [];
    if (staffDescriptors.length > 0) {
      for (const s of staffDescriptors) {
        labeled.push(new faceapi.LabeledFaceDescriptors(String(s.userId), [s.descriptor]));
      }
    }
    if (labeled.length === 0) return null;
    return new faceapi.FaceMatcher(labeled, MATCH_THRESHOLD);
  };

  // drawing helpers
  const drawBox = (ctx, box, text) => {
    const extraWidth = 40;
    const x = box.x - extraWidth / 2;
    const y = box.y;
    const width = box.width + extraWidth;
    const height = box.height;
    ctx.strokeStyle = "#00cc66";
    ctx.lineWidth = 2;
    ctx.strokeRect(x, y, width, height);
    if (text) {
      ctx.fillStyle = "rgba(0,0,0,0.6)";
      ctx.font = "14px Arial";
      const padding = 4;
      const w = ctx.measureText(text).width + padding * 10;
      const yTop = y - 20 >= 0 ? y - 20 : y + height + 6;
      ctx.fillRect(x, yTop, w, 18);
      ctx.fillStyle = "#fff";
      ctx.fillText(text, x + padding, yTop + 13);
    }
  };
  const drawText = (ctx, text, x, y, opts = {}) => {
    const { bg = "rgba(0,0,0,0.55)", padX = 6, padY = 4 } = opts;
    ctx.font = "14px Arial";
    const w = ctx.measureText(text).width + padX * 2;
    ctx.fillStyle = bg;
    ctx.fillRect(x, y - 14, w, 20);
    ctx.fillStyle = "#fff";
    ctx.fillText(text, x + padX, y + padY);
  };

  // movement liveness gating
  const lastBoxRef = useRef(null);
  const lastMoveAtRef = useRef(0);
  const lastMovementEventRef = useRef(0);
  const resetMovementState = () => {
    lastBoxRef.current = null;
    lastMoveAtRef.current = 0;
    lastMovementEventRef.current = 0;
  };
  const updateMovementState = (box) => {
    const now = Date.now();
    if (!box) return false;
    const prev = lastBoxRef.current;
    if (!prev) {
      lastBoxRef.current = { x: box.x, y: box.y, w: box.width, h: box.height };
      lastMoveAtRef.current = now;
      return false;
    }
    const dx = Math.abs(box.x - prev.x);
    const dy = Math.abs(box.y - prev.y);
    const moved = dx > MOVEMENT_THRESHOLD_PX || dy > MOVEMENT_THRESHOLD_PX;
    if (moved) {
      lastBoxRef.current = { x: box.x, y: box.y, w: box.width, h: box.height };
      lastMoveAtRef.current = now;
      if (now - lastMovementEventRef.current > MOVEMENT_COOLDOWN_MS) {
        lastMovementEventRef.current = now;
      }
      return true;
    }
    return now - lastMoveAtRef.current <= MOVEMENT_WINDOW_MS;
  };
  const hasRecentMovement = () => Date.now() - lastMoveAtRef.current <= MOVEMENT_WINDOW_MS;

  // recognition throttling
  const lastRecognizedRef = useRef(0);

  // recognition handler
  const onRecognized = async ({ userId, name }) => {
    const now = Date.now();
    if (now - lastRecognizedRef.current < RECOG_COOLDOWN_MS) return;
    lastRecognizedRef.current = now;
    stopRecognition(); // stop the loop while processing
    // face kaying marks
    try {
      const res = await Swal.fire({
        title: `Matched: ${name}`,
        text: `Do you want to Clock In or Clock Out for ${name}?`,
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonText: `Clock In`,
        denyButtonText: `Clock Out`,
      });
      if (res.isConfirmed) {
        await doClockIn({ userId, name, inMethod: "face" });
      } else if (res.isDenied) {
        await doClockOut({ userId, name, outMethod: "face" });
      } else {
        setTimeout(() => startRecognition(), 250);
      }
    } catch (err) {
      console.error("onRecognized handler error", err);
      setTimeout(() => startRecognition(), 250);
    }
  };

  // clock in/out
  const doClockIn = async ({ userId, name, inMethod }) => {
    try {
      const now = new Date();
      const payload = {
        staffId: userId,
        staffName: name,
        userId,
        name,
        inMethod,
        clockInTime: now.toISOString(),
        clockInDate: now.toISOString().split("T")[0],
      };
      const response = await axios.post(`${apiBaseUrl}/api/clockinout/clock-in`, payload);
      console.log("clock-in payload", payload);
      if (response?.data?.success) {
        Swal.fire({ icon: "success", title: "Clocked In", text: `${name} clocked in at ${now.toLocaleTimeString()}` });
      } else {
        Swal.fire({ icon: "error", title: "Clock In Failed", text: response?.data?.message || "Failed to clock in" });
      }
    } catch (err) {
      Swal.fire({ icon: "error", title: "Clock In Failed", text: err?.response?.data?.message || err?.message || "Failed to clock in" });
    }
  };
  const doClockOut = async ({ userId, name, outMethod }) => {
    try {
      const now = new Date();
      const payload = {
        staffId: userId,
        staffName: name,
        userId,
        name,
        outMethod,
        clockOutTime: now.toISOString(),
        clockOutDate: now.toISOString().split("T")[0],
      };
      console.log("clock-out payload", payload);
      const response = await axios.post(`${apiBaseUrl}/api/clockinout/clock-out`, payload);
      if (response?.data?.success) {
        Swal.fire({ icon: "success", title: "Clocked Out", text: `${name} clocked out at ${now.toLocaleTimeString()}` });
      } else {
        Swal.fire({ icon: "error", title: "Clock Out Failed", text: response?.data?.message || "Failed to clock out" });
      }
    } catch (err) {
      Swal.fire({ icon: "error", title: "Clock Out Failed", text: err?.response?.data?.message || err?.message || "Failed to clock out" });
    }
  };

  const handleClockIn = async ({ userId, name }) => await doClockIn({ userId, name });
  const handleClockOut = async ({ userId, name }) => await doClockOut({ userId, name });

  // Access Key flags (read from settings or local state)
  // NOTE: We don't alter AccessKey validation handler — we only invoke the modal here.
  const latestSettings = JSON.parse(localStorage.getItem("settings")) || {};
  const [isAccessKeyNeeded] = useState(Boolean(latestSettings.isAccessKeyNeeded));
  const [isAccessKeyValid, setIsAccessKeyValid] = useState(Boolean(localStorage.getItem("isAccessKeyValid") === "true"));

  useEffect(() => {
    // Keep state in sync if other parts of the app flip the localStorage item
    const onStorage = (e) => {
      if (e.key === "isAccessKeyValid") {
        setIsAccessKeyValid(e.newValue === "true");
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // keep existing drawer function logic intact (only ensured settings is state)
  const handleOpenCashDrawer = async () => {
    try {
      let userId;
      if (settings.isAccessKeyNeeded) {
        const operator = await accessKeyModalRef.current?.show();
        if (!operator) {
          return;
        } else {
          userId = operator?.user?.userId;
        }
      } else {
        userId = localStorage.getItem("userId");
      }

      console.log("userId: ", userId);

      // Send the payload to the backend API
      const response = await axios.post(`${config.REACT_APP_API_BASE_URL}/api/opencashdrawer`, {
        selectedPrinterId: localStorage.getItem("selectedPrinterId"),
      });

      // You can handle response here if needed (left intentionally minimal)
      // e.g. message.success("Drawer opened");
    } catch (error) {
      message.error(error?.response?.data?.message || "Failed to open drawer.");
    }
  };

  // pick largest face
  const pickPrimaryDetection = (detections) => {
    if (!detections || detections.length === 0) return null;
    let maxArea = -1;
    let chosen = detections[0];
    for (const d of detections) {
      const b = d.detection.box;
      const area = b.width * b.height;
      if (area > maxArea) {
        maxArea = area;
        chosen = d;
      }
    }
    return chosen;
  };

  // recognition loop
  const runLoop = async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) {
      rafRef.current = requestAnimationFrame(runLoop);
      return;
    }
    if (video.readyState < 2) {
      // not enough data
      rafRef.current = requestAnimationFrame(runLoop);
      return;
    }
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const displaySize = { width: canvas.width, height: canvas.height };
    try {
      const detections = await faceapi
        .detectAllFaces(video, new faceapi.TinyFaceDetectorOptions(TINY_OPTIONS))
        .withFaceLandmarks()
        .withFaceDescriptors();
      const resized = faceapi.resizeResults(detections, displaySize);
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (resized.length > 0) {
        const primary = pickPrimaryDetection(resized);
        if (primary) {
          const box = primary.detection.box;
          const moved = updateMovementState(box);
          drawText(ctx, `Movement: ${moved ? "OK" : "Please move"}`, 10, 24);
          for (const d of resized) drawBox(ctx, d.detection.box);
          if (moved) {
            const matcher = buildFaceMatcher();
            if (matcher) {
              const best = matcher.findBestMatch(primary.descriptor);
              const label = best.label;
              if (label !== "unknown") {
                const staffHit = staffDescriptors.find((sd) => String(sd.userId) === String(label));
                if (staffHit) {
                  drawBox(ctx, box, `${staffHit.name} (${staffHit.userId})`);
                  await onRecognized({ userId: staffHit.userId, name: staffHit.name });
                  return;
                } else {
                  drawBox(ctx, box, best.toString());
                }
              } else {
                drawBox(ctx, box, best.toString());
              }
            } else {
              drawText(ctx, "Preparing staff dataset...", 10, 48);
            }
          } else {
            drawBox(ctx, box, "Liveness required: move head");
          }
        }
      } else {
        drawText(ctx, "No face detected", 10, 24);
      }
    } catch (err) {
      console.error("runLoop error", err);
    }
    rafRef.current = requestAnimationFrame(runLoop);
  };

  // recognition control
  const [isRecognizing, setIsRecognizing] = useState(false);
  const startRecognition = async () => {
    // ensure only one caller starts models + webcam
    const ok = await ensureModels();
    if (!ok) return;

    resetMovementState();
    lastRecognizedRef.current = 0;

    // build descriptors if empty
    if (staffList.length === 0 && !loadingStaff) {
      const list = await fetchStaffList();
      await computeStaffDescriptorsLocal(list);
      console.log("staff descriptors computed after fetch", list);
    } else if (staffDescriptors.length === 0 && staffList.length > 0) {
      await computeStaffDescriptorsLocal(staffList);
    }

    try {
      await startWebcam();
    } catch {
      return;
    }
    setIsRecognizing(true);
    // small delay to allow video to initialize
    rafRef.current = requestAnimationFrame(runLoop);
  };
  const stopRecognition = () => {
    try {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    } catch (e) { }
    stopWebcam();
    setIsRecognizing(false);
    const c = canvasRef.current;
    if (c?.getContext) c.getContext("2d").clearRect(0, 0, c.width, c.height);
  };

  const [attendanceVisible, setAttendanceVisible] = useState(false);
  const attendanceOpeningRef = useRef(false);

  const handleAttendanceClick = async () => {
    try {
      if (attendanceOpeningRef.current) return;
      attendanceOpeningRef.current = true;

      setAttendanceVisible(true);
      // warm up models and staff descriptors in the background
      await ensureModels();
      if (staffList.length === 0 && !loadingStaff) {
        const list = await fetchStaffList();
        await computeStaffDescriptorsLocal(list);
      } else if (staffDescriptors.length === 0 && staffList.length > 0) {
        await computeStaffDescriptorsLocal(staffList);
      }

      setTimeout(() => {
        // noop - keep quiet. user should press Start Scanning to begin.
      }, 120);
    } catch (err) {
      console.error("handleAttendanceClick err", err);
      message.error("Failed to open Attendance modal");
    } finally {
      attendanceOpeningRef.current = false;
    }
  };

  // Close attendance modal: stop recognition and hide modal (but keep mounted)
  const closeAttendanceModal = () => {
    setAttendanceVisible(false);
    stopRecognition();
  };

  // === Admin Access Key (Use logged-in password instead of hardcoded key)
  const ADMIN_ACCESS_KEY = sessionStorage.getItem("userPassword");

  // Normalize user data from AccessKeyModal
  const normalizeAccessUser = (u) => {
    if (!u) return null;
    const nestedUser = u.user ?? u;
    const id = String(
      nestedUser.userId ??
      nestedUser._id ??
      nestedUser.id ??
      nestedUser.staffId ??
      ""
    );
    const name =
      nestedUser.name ??
      nestedUser.username ??
      nestedUser.staffName ??
      "Unknown";
    return { userId: id, name, raw: u };
  };

  // Perform auto clock (unchanged)
  const performAutoClock = async (userId, name) => {
    const res = await Swal.fire({
      title: `Attendance action for ${name}`,
      text: "Choose action:",
      showDenyButton: true,
      showCancelButton: true,
      confirmButtonText: "Clock In",
      denyButtonText: "Clock Out",
    });

    if (res.isConfirmed)
      return await doClockIn({ userId, name, inMethod: "accessKey" });

    if (res.isDenied)
      return await doClockOut({ userId, name, outMethod: "accessKey" });

    return null;
  };

  // Handle Access Key result after admin validated
  const handleAccessKeyResult = async (rawUser) => {
    try {
      const normalized = normalizeAccessUser(rawUser);
      if (!normalized || !normalized.userId) {
        message.error("Unknown Staff");
        return;
      }

      log("Access Key selected", normalized);
      await performAutoClock(normalized.userId, normalized.name);
    } catch (err) {
      console.error("handleAccessKeyResult err", err);
      message.error("Access Key attendance failed");
    }
  };

  // Open Access Key flow with admin key prompt
  const openAccessKeyFlow = async () => {
    try {
      // Step 1: Ask for admin key
      const { value: adminKey } = await Swal.fire({
        title: "Admin Validation",
        input: "password",
        inputLabel: "Please enter Admin Key",
        inputPlaceholder: "Admin Key",
        inputAttributes: {
          autocapitalize: "off",
          autocorrect: "off",
        },
        showCancelButton: true,
      });

      if (!adminKey) return; // cancelled

      if (adminKey !== ADMIN_ACCESS_KEY) {
        message.error("Invalid Admin Key");
        return;
      }

      message.success("Admin Key verified");

      // Step 2: Open Access Key modal
      if (!accessKeyModalRef.current) {
        message.error("Access Key modal not available");
        return;
      }

      let userData;
      if (typeof accessKeyModalRef.current.show === "function") {
        userData = await accessKeyModalRef.current.show();
      } else if (typeof accessKeyModalRef.current.open === "function") {
        accessKeyModalRef.current.open();
        return;
      } else if (typeof accessKeyModalRef.current.showOrOpen === "function") {
        userData = await accessKeyModalRef.current.showOrOpen();
      }

      if (userData) {
        await handleAccessKeyResult(userData);
      }
    } catch (err) {
      console.error("openAccessKeyFlow err", err);
      message.error("Failed to open Access Key");
    }
  };

  // Expose to parent via ref
  useImperativeHandle(ref, () => ({
    openAccessKey: () => openAccessKeyFlow(),
    openFaceRecognition: () => handleAttendanceClick(),
  }));


  // staff dataset status component
  const StaffDatasetStatus = () => (
    <div style={{ marginTop: 8, border: `1px solid ${COLORS.lightBorder}`, borderRadius: 8, padding: 10 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <div style={{ fontWeight: 600 }}>Staff dataset</div>
        <Tag color={staffDescriptors.length ? "blue" : "default"}>Descriptors: {staffDescriptors.length}</Tag>
      </div>
      <div style={{ marginTop: 8, color: COLORS.grayText, fontSize: 13 }}>
        {staffDescriptors.length > 0 ? "Dataset ready for matching." : "No descriptors prepared."}
      </div>
    </div>
  );

  useEffect(() => {
    if (areModelsLoaded()) {
      setModelsLoaded(true);
      setModelsLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // prefetch staff list so later descriptor compute is fast
      try {
        await fetchStaffList();
        // do not compute descriptors now; wait until user opens Attendance or explicitly start recognition
      } catch (err) {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Render
  return (
    <ModalBlurContext.Provider value={{ incrementModal, decrementModal }}>
      <Layout style={{ minHeight: "100vh", marginLeft: collapsed ? 80 : 180 }}>
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          width={180}
          collapsedWidth={80}
          style={{
            background: "white",
            boxShadow: "2px 0 8px rgba(0,0,0,0.1)",
            borderRight: `1px solid ${COLORS.lightBorder}`,
            overflow: "auto",
            height: "100vh",
            position: "fixed",
            left: 0,
            transition: "width 0.3s",
            fontFamily: "Inter, sans-serif",
          }}
        >
          {/* Fixed/stable logo container */}
          <div
            className="logo text-center my-4 collapsed-sidebar-header-title"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              paddingTop: 16,
              paddingBottom: 16,
              willChange: "transform",
            }}
            aria-hidden="true"
          >
            <img
              src={logo}
              alt="Logo"
              draggable={false}
              style={{
                width: "100px",
                transition: "width 0.3s",
                pointerEvents: "none",
                userSelect: "none",
              }}
            />
          </div>

          <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            defaultSelectedKeys={[window.location.pathname]}
            openKeys={openKeys}
            onOpenChange={onOpenChange}
            style={{ backgroundColor: "transparent", color: COLORS.text, fontWeight: 500 }}
            theme="light"
          >
            <SubMenu key="POS" title="P.O.S" icon={<HomeOutlined />}>
              <Menu.Item key="/">
                <Link to="/">Table</Link>
              </Menu.Item>
              <Menu.Item key="/cashiers">
                <Link to="/cashiers">Cashiers</Link>
              </Menu.Item>
              <Menu.Item key="/staffTransactionsRecord">
                <Link to="/staffTransactionsRecord">Transaction</Link>
              </Menu.Item>
            </SubMenu>

            <Menu.Item key="/report" icon={<FileTextOutlined />}>
              <Link to="/report">Report</Link>
            </Menu.Item>

            <Menu.Item
              key="secondDisplay"
              icon={<LaptopOutlined />}
              onClick={() => {
                if (window.electronAPI) {
                  console.log("Electron API branch triggered");
                  window.electronAPI.openWindow("secondDisplay");
                } else {
                  console.log("Fallback branch (window.open) triggered");
                  window.open("#/secondDisplay", "_blank");
                }
              }}
            >
              Second Display
            </Menu.Item>


            <Menu.Item key="/kitchen" icon={<DesktopOutlined />}>
              <Link to="/kitchen">Kitchen</Link>
            </Menu.Item>

            {getUserRole() === "admin" && (
              <SubMenu key="SubMenu" title="Settings" icon={<SettingOutlined />}>
                <Menu.Item key="/general">
                  <Link to="/general">General</Link>
                </Menu.Item>
                <Menu.Item key="/preferences">
                  <Link to="/preferences">Preferences</Link>
                </Menu.Item>
                <Menu.Item key="/tables">
                  <Link to="/tables">Tables</Link>
                </Menu.Item>
                <Menu.Item key="/category">
                  <Link to="/category">Category</Link>
                </Menu.Item>
                <Menu.Item key="/items">
                  <Link to="/items">Menu</Link>
                </Menu.Item>
                <Menu.Item key="/inventory">
                  <Link to="/inventory">Inventory</Link>
                </Menu.Item>
                <Menu.Item key="/inventoryLog">
                  <Link to="/inventoryLog">Inventory Log</Link>
                </Menu.Item>
                <Menu.Item key="/setTax">
                  <Link to="/setTax">Taxes</Link>
                </Menu.Item>
                <Menu.Item key="/setService">
                  <Link to="/setService">Services</Link>
                </Menu.Item>
                <Menu.Item key="/staff">
                  <Link to="/staff">Staff</Link>
                </Menu.Item>
                <Menu.Item key="/clockInOut">
                  <Link to="/clockInOut">Attendance</Link>
                </Menu.Item>
                <Menu.Item key="/productIcon">
                  <Link to="/productIcon">Product Icon</Link>
                </Menu.Item>
              </SubMenu>
            )}
          </Menu>
        </Sider>

        <Layout className="site-layout">
          <Header
            className="site-layout-background"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "0 34px",
              height: "64px",
              background: "#fff",
              position: "fixed",
              top: 0,
              right: 0,
              left: collapsed ? 80 : 180,
              zIndex: 10,
              borderBottom: `1px solid ${COLORS.lightBorder}`,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "15px", minWidth: 0 }}>
              <div className="expand" style={{ display: "flex", justifyContent: "end" }}>
                {collapsed === false ? (
                  <MenuFoldOutlined onClick={toggleCollapse} style={{ cursor: "pointer", color: "black", fontSize: 18 }} />
                ) : (
                  <MenuUnfoldOutlined onClick={toggleCollapse} style={{ cursor: "pointer", color: "black", fontSize: 18 }} />
                )}
              </div>

              {customSearchComponent ? (
                customSearchComponent
              ) : (
                showSearch && (
                  <Input
                    className="responsive-searchbar"
                    placeholder={searchPlaceholder || "Search"}
                    prefix={<SearchOutlined />}
                    onChange={handleSearch}
                    value={searchText}
                    style={{ width: "clamp(120px, 20vw, 350px)", borderRadius: 8, minWidth: 0, maxWidth: "100%", flex: 1 }}
                    allowClear
                  />
                )
              )}

              {showSlug && <div style={{ marginLeft: 16 }}>{showSlug}</div>}
            </div>

            <div className="header-right" style={{ display: "flex", alignItems: "center", gap: 8, flex: "0 0 auto" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <PrinterOutlined style={{ fontSize: 20 }} />
                <Select
                  value={selectedPrinterId}
                  onChange={handlePrinterChange}
                  onDropdownVisibleChange={handlePrinterStatusDropdownVisibleChange}
                  suffixIcon={printerStatusOpen ? <CaretUpOutlined /> : <CaretDownOutlined />}
                  getPopupContainer={(trigger) => document.body}
                  dropdownStyle={{ minWidth: 220 }}
                >
                  {printersData.map((p) => (
                    <Select.Option key={p.id} value={p.id}>
                      <Tooltip title={p.name} placement="right">
                        {p.name}
                      </Tooltip>
                    </Select.Option>
                  ))}
                </Select>
              </div>
              {/* open cash drawer */}
              <Button
                type={"default"}
                onClick={() => {
                  handleOpenCashDrawer();
                }}
                style={{
                  height: 32,
                  minWidth: 120,
                  borderRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "#fff",
                  color: "#dc6c04",
                  borderColor: "#dc6c04",
                  boxShadow: "none",
                  transition: "all 0.2s",
                  padding: "0 11px",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <DownSquareOutlined style={{ fontSize: "13px" }} />
                  Drawer
                </div>
              </Button>

              {/* Buttons cluster */}
              {/* <div style={{ display: "flex", gap: 8, alignItems: "center" }}> */}
              <Button
                type="primary"
                onClick={handleAttendanceClick}
                style={{
                  height: 32,
                  minWidth: 120,
                  borderRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: COLORS.brand,
                  color: COLORS.white,
                  borderColor: COLORS.brandText,
                  boxShadow: "0 2px 8px rgba(229,103,23,0.15)",
                  transition: "all 0.2s",
                  padding: "0 11px",
                }}
              >
                Attendance
              </Button>
              {/* </div> */}

              <Dropdown menu={{ items: menuItems }} placement="bottomRight">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    borderRadius: 4,
                    color: isUserHovered ? COLORS.brand : "black",
                    transition: "color 0.3s",
                    height: 40,
                  }}
                  onMouseEnter={() => setIsUserHovered(true)}
                  onMouseLeave={() => setIsUserHovered(false)}
                >
                  <UserOutlined style={{ marginRight: 5, fontSize: 20, verticalAlign: "middle" }} />
                  <span style={{ fontSize: 16, lineHeight: "40px" }}>{currentUser ? capitalize(currentUser) : "User"}</span>
                </div>
              </Dropdown>
            </div>

            {/* Attendance Modal: keep mounted to preserve refs and avoid reloading models on route change. */}
            <Modal
              title={
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span>AI Face Recognition System</span>
                    <Tooltip title="Open modal then click 'Start Scanning' to begin. Move your head slightly to verify liveness.">
                      <InfoCircleOutlined style={{ color: COLORS.grayText }} />
                    </Tooltip>
                  </div>
                  <CloseButton onClick={closeAttendanceModal} />
                </div>
              }
              open={attendanceVisible}
              onCancel={closeAttendanceModal}
              footer={null}
              destroyOnClose={false}
              width={610}
              maskClosable={false}
            >
              <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <div style={{ position: "relative", borderRadius: 8 }}>
                  <video
                    ref={videoRef}
                    autoPlay
                    muted
                    playsInline
                    style={{
                      width: 560,
                      height: Math.round((560 * 3) / 4),
                      background: "#000",
                      borderRadius: 8,
                    }}
                  />
                  <canvas
                    ref={canvasRef}
                    style={{
                      position: "absolute",
                      left: 0,
                      top: 0,
                      width: "100%",
                      height: "100%",
                      pointerEvents: "none",
                    }}
                  />
                  <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                      <strong>Status</strong>
                      <Tag color={modelsLoaded ? "processing" : "default"}>Models: {modelsLoaded ? "Loaded" : modelsLoading ? "Loading..." : "Not loaded"}</Tag>
                      <Tag color={isRecognizing ? "green" : "default"}>Webcam: {isRecognizing ? "Scanning" : "No Access"}</Tag>
                      <Tag color={staffDescriptors.length ? "blue" : "default"}>Staff Descriptors: {staffDescriptors.length}</Tag>
                      <Tag color={hasRecentMovement() ? "success" : "default"}>Movement: {hasRecentMovement() ? "OK" : "Required"}</Tag>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                      <Button onClick={startRecognition} disabled={isRecognizing || modelsLoading}>
                        Start Scanning
                      </Button>
                      <Button onClick={stopRecognition} danger disabled={!isRecognizing}>
                        Stop Scanning
                      </Button>

                      <Button onClick={openAccessKeyFlow}>Use Access Key</Button>
                      <Button
                        onClick={async () => {
                          // Manual models reload attempt (useful if models failed earlier)
                          setModelsLoading(true);
                          const ok = await loadFaceModelsOnce();
                          setModelsLoaded(ok);
                          setModelsLoading(false);
                          if (ok) message.success("Face models loaded");
                          else message.error("Failed to load models");
                        }}
                        type="default"
                      >
                        Reload Models
                      </Button>
                    </div>

                    <StaffDatasetStatus />
                  </div>
                </div>
              </div>
            </Modal>

            <AccessKeyModal ref={accessKeyModalRef} apiBaseUrl={apiBaseUrl} onResolve={handleAccessKeyResult} />
          </Header>

          <Content
            className={`site-layout-background${openModalCount > 0 ? " blur-background" : ""}`}
            style={{
              marginTop: 64,
              padding: "30px 10px 0 15px",
              transition: "margin-left 0.2s ease",
              minHeight: "calc(100vh - 64px)",
              overflow: "auto",
              ...contentStyle,
            }}
          >
            {children}
          </Content>
        </Layout>
      </Layout>
    </ModalBlurContext.Provider>
  );
});

export default DefaultLayout;
